'use client'

import {
  accountReadiness,
  onAnalyticsSdkReady,
  updateAccountReadiness,
} from '@/lib/analytics/account-readiness'
import { useEffect, useRef } from 'react'
import posthog from '@/lib/analytics/posthog-lazy'
import { isAuthSessionMissingError } from '@supabase/supabase-js'
import { createClient } from '@/lib/supabase/client'
import { getAnalyticsMode, onAnalyticsModeChange } from '@/lib/analytics/consent'
import {
  applySignedInAnswer,
  applySignedOut,
  readAccountAnswer,
  setSignedInUser,
} from '@/lib/analytics/account-consent'

/**
 * Applies the signed-in member's analytics answer and identifies them in
 * PostHog so sessions are linked to real persons. Rendered once in the root
 * layout.
 *
 * On every page load and sign-in it reads the account's answer (Settings →
 * Privacy & Consent) and switches PostHog to it (lib/analytics/account-consent.ts):
 * signed in, the account decides, in the app and on the web. On sign-out the
 * web banner (or, in the app, nothing) decides again.
 *
 * Identifies only in `full` mode and only once the account's answer is
 * known, so a device that starts full from an old answer never identifies
 * someone who has since said no. Going back to anonymous has already reset
 * PostHog, so the next identify starts over.
 *
 * Only the Supabase user id is sent — no email, no name, no other person
 * properties (measurement wave 1: PostHog is a third party and doesn't need
 * PII; the school and role travel as super properties from <AnalyticsContext>).
 * Person properties set by older builds stay on PostHog until cleaned there.
 */
export function PostHogIdentify() {
  const identifiedRef = useRef<string | null>(null)

  useEffect(() => {
    const supabase = createClient()
    // `undefined` until the first answer from auth, then the user id or null.
    let userId: string | null | undefined = undefined
    // The user whose account answer has been applied.
    let resolvedFor: string | null = null
    let run = 0
    let disposed = false

    function sync() {
      if (disposed) return
      if (getAnalyticsMode() !== 'full') {
        identifiedRef.current = null
        const ready = accountReadiness()
        if (ready.identified) updateAccountReadiness({ ...ready, identified: false })
        return
      }
      if (!posthog.__loaded) return
      if (userId && userId === resolvedFor && userId !== identifiedRef.current) {
        posthog.identify(userId)
        identifiedRef.current = userId
        updateAccountReadiness({ userId, resolved: true, identified: true })
      } else if (!userId && identifiedRef.current) {
        posthog.reset()
        identifiedRef.current = null
      }
    }

    async function onUser(next: string | null) {
      if (disposed) return
      // Token refreshes and the duplicate initial event change nothing.
      if (next === userId) return
      if (identifiedRef.current && identifiedRef.current !== next && posthog.__loaded)
        posthog.reset()
      identifiedRef.current = null
      resolvedFor = null
      userId = next
      setSignedInUser(next)
      const current = ++run

      if (!next) {
        resolvedFor = null
        sync()
        applySignedOut(posthog)
        updateAccountReadiness({ userId: null, resolved: true, identified: false })
        return
      }

      const answer = await readAccountAnswer(supabase, next)
      if (disposed || current !== run) return
      // Unreadable: keep the mode this load started in, identify nobody.
      if (!answer) return
      applySignedInAnswer(posthog, next, answer)
      resolvedFor = next
      updateAccountReadiness({ userId: next, resolved: true, identified: false })
      sync()
    }

    void supabase.auth
      .getUser()
      .then(({ data: { user }, error }) => {
        if (error && !isAuthSessionMissingError(error)) return
        if (!disposed && userId === undefined) return onUser(user?.id ?? null)
      })
      .catch(() => {
        if (disposed || userId !== undefined) return
        updateAccountReadiness({ userId: null, resolved: false, identified: false })
      })

    // Re-check on auth state changes (login, logout)
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') return
      void onUser(session?.user?.id ?? null)
    })

    const unsubscribeMode = onAnalyticsModeChange(sync)
    const unsubscribeSdk = onAnalyticsSdkReady(sync)

    return () => {
      disposed = true
      ++run
      subscription.unsubscribe()
      unsubscribeMode()
      unsubscribeSdk()
      updateAccountReadiness({ userId: null, resolved: false, identified: false })
    }
  }, [])

  return null
}
